import { checkIsLoop, checkIsLoopEnd, checkIsLoopStep } from "@/core/util";
import {
  findRouterByRouteId,
  getRouterLoopContext,
} from "@/flow/utils/routerUtils";
import { findStepByOrigId } from "@/flow/utils/nodeUtils";
import { parseUiData } from "@/flow/utils/uiDataUtils";

export type LoopConfig = {
  parallelProcessing?: boolean;
  threadCount?: number;
  count?: string;
  // OT-4718 Step 7 — BATCH group size N (chunk the source array into groups of ≤ N). Only carried for
  // BATCH loops; the backend requires it to be >= 1.
  batchSize?: number;
};

export type LoopType =
  | "SPLIT"
  | "FOR"
  | "WHILE"
  | "DO_WHILE"
  | "BATCH"
  | "PAGINATION";

export const DEFAULT_LOOP_TYPE: LoopType = "SPLIT";

/**
 * The loop types, in the order the Loop Settings dropdown offers them.
 *
 * This is the list that used to live inside EditLoop.vue. It sits here so the drawer and every
 * read-only description of a loop name the types identically — a new loop type is added once, and
 * nothing has to invent its own wording. `labelKey` rather than a translated string keeps this
 * module free of i18n; callers translate at the point of use.
 */
export const LOOP_TYPE_OPTIONS: Array<{
  value: LoopType;
  labelKey: string;
  enabled: boolean;
}> = [
  { value: "SPLIT", labelKey: "Common.LoopOptionSplit", enabled: true },
  { value: "FOR", labelKey: "Common.LoopOptionFor", enabled: true },
  // WHILE and DO_WHILE are presented to the user as a single "conditional" loop (the WHILE vs DO_WHILE
  // choice is a sub-dropdown in the loop drawer), so both name the same merged label.
  { value: "WHILE", labelKey: "Common.LoopOptionConditional", enabled: true },
  {
    value: "DO_WHILE",
    labelKey: "Common.LoopOptionConditional",
    enabled: true,
  },
  { value: "BATCH", labelKey: "Common.LoopOptionBatch", enabled: true },
  {
    value: "PAGINATION",
    labelKey: "Common.LoopOptionPagination",
    enabled: false,
  },
];

/**
 * Loop types as older flows spelled them, still present on steps saved before the current values.
 */
const LEGACY_LOOP_TYPE_MAP: Record<string, LoopType> = {
  "for-each": "SPLIT",
  for: "FOR",
  while: "WHILE",
  "do-while": "DO_WHILE",
  "batch-loop": "BATCH",
};

/**
 * The canonical loop type for whatever a step carries — current value, legacy spelling, or nothing.
 *
 * Anything that reads `step.loopType` must go through this. A saved step may hold `"for"` rather
 * than `"FOR"`, and a reader that only matched the current values would quietly fall back to SPLIT
 * and disagree with the Loop Settings drawer about what the loop does.
 */
export function normalizeLoopType(value: unknown): LoopType {
  if (typeof value !== "string" || value === "") {
    return DEFAULT_LOOP_TYPE;
  }

  const current = LOOP_TYPE_OPTIONS.find((option) => option.value === value);
  if (current) {
    return current.value;
  }
  // hasOwnProperty, not a bare lookup: LEGACY_LOOP_TYPE_MAP is an object literal, so it inherits
  // Object.prototype. A step carrying loopType "constructor" / "toString" / "valueOf" would
  // otherwise resolve to a function, which `??` happily accepts — and callers compare and render
  // whatever comes back.
  if (Object.prototype.hasOwnProperty.call(LEGACY_LOOP_TYPE_MAP, value)) {
    return LEGACY_LOOP_TYPE_MAP[value];
  }
  return DEFAULT_LOOP_TYPE;
}

/** The label key for a loop, resolving legacy spellings exactly as the drawer does. */
export function loopTypeLabelKey(loopType: unknown): string {
  const normalized = normalizeLoopType(loopType);
  // normalizeLoopType only ever returns a value that is in the list, so this always resolves.
  const option = LOOP_TYPE_OPTIONS.find(({ value }) => value === normalized);
  return option?.labelKey ?? LOOP_TYPE_OPTIONS[0].labelKey;
}

export const DEFAULT_LOOP_THREAD_COUNT = 1;
export const DEFAULT_FOR_LOOP_COUNT = "[]";
// OT-4718 Step 7 — BATCH default group size. Mirrors the thread-count default (a valid minimum, min 1);
// the user sets the real N in the Batch Size field. The backend rejects a missing/< 1 batchSize.
export const DEFAULT_BATCH_SIZE = 1;

export function getDefaultLoopConfig(): LoopConfig {
  return {
    parallelProcessing: false,
    threadCount: DEFAULT_LOOP_THREAD_COUNT,
  };
}

export function isParallelProcessingLoopType(loopType: unknown): boolean {
  return loopType === "SPLIT" || loopType === "BATCH";
}

/**
 * Predicate-driven loops (WHILE / DO_WHILE). These iterate on a condition (the LOOP step's `filter`),
 * not over a source array — so they have no "property to iterate over" and no parallel processing.
 */
export function isConditionalLoopType(loopType: unknown): boolean {
  return loopType === "WHILE" || loopType === "DO_WHILE";
}

/**
 * Loop types whose behaviour is driven by the LOOP step's `filter` (a condition): WHILE / DO_WHILE
 * (the predicate) and PAGINATION (the continue condition). SPLIT / FOR / BATCH carry no filter, so the
 * predicate must be cleared when switching to one of them.
 */
export function loopTypeUsesFilter(loopType: unknown): boolean {
  return (
    loopType === "WHILE" || loopType === "DO_WHILE" || loopType === "PAGINATION"
  );
}

/**
 * Whether changing a loop from `previousLoopType` to `nextLoopType` must clear the LOOP step's predicate
 * (`filter`). True only when leaving a filter-driven loop (WHILE / DO_WHILE / PAGINATION) for one with no
 * condition (SPLIT / FOR / BATCH).
 *
 * Keyed on the type transition, never on the current filter contents: the drawer's view of the filter is a
 * stale shallow copy of `selectedStep`, so a predicate added earlier in the same session is invisible to it
 * — reading it would let a predicate edited on an initially-empty WHILE/DO_WHILE loop survive the switch to
 * a non-conditional loop (OT-4718 / CodeRabbit).
 */
export function shouldClearLoopPredicate(
  previousLoopType: unknown,
  nextLoopType: unknown
): boolean {
  return (
    loopTypeUsesFilter(previousLoopType) && !loopTypeUsesFilter(nextLoopType)
  );
}

function toPositiveInteger(value: unknown): number {
  const parsed =
    typeof value === "number" ? value : parseInt(String(value ?? ""), 10);

  if (!Number.isFinite(parsed) || parsed < DEFAULT_LOOP_THREAD_COUNT) {
    return DEFAULT_LOOP_THREAD_COUNT;
  }

  return Math.floor(parsed);
}

export function normalizeLoopConfig(
  loopConfig: any,
  loopType: unknown
): LoopConfig {
  if (loopType === "FOR") {
    const count = loopConfig?.count;

    return {
      count:
        count === undefined || count === null
          ? DEFAULT_FOR_LOOP_COUNT
          : String(count),
    };
  }

  if (!isParallelProcessingLoopType(loopType)) {
    return getDefaultLoopConfig();
  }

  const legacyThreads = toPositiveInteger(loopConfig?.threads);
  const threadCount = toPositiveInteger(
    loopConfig?.threadCount ?? (legacyThreads > 1 ? legacyThreads : undefined)
  );
  const parallelProcessing =
    loopConfig?.parallelProcessing === true ||
    (loopConfig?.parallelProcessing === undefined && legacyThreads > 1);

  const config: LoopConfig = parallelProcessing
    ? { parallelProcessing: true, threadCount }
    : getDefaultLoopConfig();

  // OT-4718 Step 7 — BATCH additionally carries a group size N (>= 1) alongside the parallel settings.
  // SPLIT never gets a batchSize.
  if (loopType === "BATCH") {
    config.batchSize = toPositiveInteger(loopConfig?.batchSize);
  }

  return config;
}

export function toThreadBudget(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
    return undefined;
  }

  return parsed;
}

/**
 * Reads the per-execution parallel thread budget. Execution settings are
 * returned in a nested map by the API, while some callers expose the value
 * directly on the execution object.
 */
export function getExecutionParallelConcurrency(
  execution: any
): number | undefined {
  const directValue = toThreadBudget(
    execution?.parallelConcurrency?.value ?? execution?.parallelConcurrency
  );
  if (directValue !== undefined) {
    return directValue;
  }

  const executionSettings = execution?.executionSettings;
  if (!executionSettings || typeof executionSettings !== "object") {
    return undefined;
  }

  return (Object.values(executionSettings) as any[])
    .map((setting) => {
      const definition =
        setting?.executionSettingDefinitions?.parallelConcurrency ??
        setting?.parallelConcurrency;
      return toThreadBudget(definition?.value ?? definition);
    })
    .find((value) => value !== undefined);
}

/**
 * Recursively sums every parallel LOOP's worker count, including loops nested
 * below router routes. A missing loop type is treated as SPLIT to match the
 * backend's legacy-loop behavior.
 */
export function sumParallelLoopThreadCount(stepList: any[]): number {
  if (!Array.isArray(stepList)) return 0;

  let total = 0;
  const visitedSteps = new WeakSet<Record<string, unknown>>();
  const countedLoopIds = new Set<string>();

  const visit = (steps: any[]): void => {
    if (!Array.isArray(steps)) return;

    steps.forEach((step) => {
      if (!step || typeof step !== "object") return;
      if (visitedSteps.has(step)) return;
      visitedSteps.add(step);

      if (checkIsLoop(step) || step.type === "LOOP") {
        const loopId = step.origId ?? step.id;
        const alreadyCounted =
          loopId !== undefined && countedLoopIds.has(String(loopId));

        if (!alreadyCounted) {
          const loopConfig = normalizeLoopConfig(
            step.loopConfig,
            step.loopType ?? "SPLIT"
          );
          if (loopConfig.parallelProcessing) {
            total += loopConfig.threadCount ?? DEFAULT_LOOP_THREAD_COUNT;
          }
          if (loopId !== undefined) {
            countedLoopIds.add(String(loopId));
          }
        }
      }

      if (Array.isArray(step.routes)) {
        step.routes.forEach((route: any) => visit(route?.steps));
      }
      visit(step.steps);
    });
  };

  visit(stepList);
  return total;
}

/**
 * Find the parent loop for a step at the given index.
 * Accounts for nested loops by tracking LOOP_END boundaries.
 */
export function findPreviousLoopStep(
  steps: any[],
  currentIndex: number
): any | null {
  let loopEndCount = 0;

  for (let i = currentIndex - 1; i >= 0; i--) {
    const step = steps[i];

    if (checkIsLoopEnd(step)) {
      loopEndCount++;
    } else if (checkIsLoop(step)) {
      if (loopEndCount > 0) {
        loopEndCount--;
      } else {
        return step;
      }
    }
  }

  return null;
}

/**
 * Get loop context for a step
 * Uses position-based detection with LOOP_END boundary tracking
 */
export function getLoopContextForStep(
  step: any,
  stepList: any[]
): {
  inLoop: boolean;
  loopOrigId: string | null;
  loopEndOrigId: string | null;
} {
  const noContext = { inLoop: false, loopOrigId: null, loopEndOrigId: null };

  if (!step) return noContext;

  // Case 1: Step itself is a LOOP - not "inside" a loop
  if (checkIsLoop(step)) {
    return noContext;
  }

  // Case 2: Step is inside a router - check router's loop context
  if (step.parentId) {
    const router = findRouterByRouteId(stepList, step.parentId);
    if (router) {
      const routerContext = getRouterLoopContext(router);
      if (routerContext.insideLoop && routerContext.loopEndOrigId) {
        return {
          inLoop: true,
          loopOrigId: routerContext.parentLoopOrigId || null,
          loopEndOrigId: routerContext.loopEndOrigId,
        };
      }
    }
  }

  // Case 3: Find parent loop using position-based detection
  // This accounts for nested loops by tracking LOOP_END boundaries
  const stepResult = findStepByOrigId(stepList, step.origId);
  if (!stepResult) return noContext;

  const { steps, index } = stepResult;
  const parentLoop = findPreviousLoopStep(steps, index);

  if (parentLoop && parentLoop.uiData) {
    try {
      const loopUiData = JSON.parse(parentLoop.uiData);
      return {
        inLoop: true,
        loopOrigId: parentLoop.origId,
        loopEndOrigId: loopUiData.loopEndOrigId || null,
      };
    } catch {
      // ignore parse error
    }
  }

  return noContext;
}

export function checkIsLastLoopStep(loop, step) {
  if (!loop || !loop.uiData) return false;

  try {
    const uiData = JSON.parse(loop.uiData);
    if (uiData.steps && Array.isArray(uiData.steps)) {
      const lastStep = uiData.steps[uiData.steps.length - 1];
      return lastStep?.origId === step.origId;
    }
  } catch (e) {
    console.warn("Failed to parse loop uiData:", e);
  }
  return false;
}

/**
 * Gets the loop end origId from a loop step's uiData.
 */
export function getLoopEndOrigId(
  step: { uiData?: string } | null | undefined
): string | null {
  const uiData = parseUiData(step?.uiData);
  return uiData?.loopEndOrigId ?? null;
}

/**
 * Gets the loop start origId from a loop step or loop end's uiData.
 */
export function getLoopStartOrigId(
  step: { uiData?: string } | null | undefined
): string | null {
  const uiData = parseUiData(step?.uiData);
  return uiData?.loopStartOrigId ?? uiData?.loopStepOrigId ?? null;
}

/**
 * Gets the parent loop origId for a step inside a loop.
 */
export function getParentLoopOrigId(
  step: { uiData?: string } | null | undefined
): string | null {
  const uiData = parseUiData(step?.uiData);
  return (
    uiData?.loopStepOrigId ??
    uiData?.loopStartOrigId ??
    uiData?.parentLoopOrigId ??
    null
  );
}

/**
 * Gets the loop stack (outer -> inner) for a step.
 * Includes router loop context when the step is inside a router.
 */
export function getLoopStackForStep(step: any, stepList: any[]): string[] {
  if (!step) return [];

  const buildChain = (startId: string | null | undefined): string[] => {
    if (!startId) return [];
    const chain: string[] = [];
    const visited = new Set<string>();
    let current = startId;

    while (current) {
      if (visited.has(current)) break;
      visited.add(current);
      chain.push(current);

      const found = Array.isArray(stepList)
        ? findStepByOrigId(stepList, current)
        : null;
      if (!found?.step) break;

      const parentId =
        getLoopStartOrigId(found.step) ?? found.step?.parentLoopOrigId;
      if (!parentId || parentId === current) break;
      current = parentId;
    }

    return chain.reverse();
  };

  let routerChain: string[] = [];
  if (step?.insideLoop && step?.parentLoopOrigId) {
    routerChain = buildChain(step.parentLoopOrigId);
  }
  if (!routerChain.length && step.parentId && Array.isArray(stepList)) {
    const router = findRouterByRouteId(stepList, step.parentId);
    const context = getRouterLoopContext(router);
    if (context.parentLoopOrigId) {
      routerChain = buildChain(context.parentLoopOrigId);
    }
  }

  const stepChain = buildChain(getLoopStartOrigId(step));
  const merged: string[] = [];

  routerChain.forEach((id) => {
    if (!merged.includes(id)) merged.push(id);
  });
  stepChain.forEach((id) => {
    if (!merged.includes(id)) merged.push(id);
  });

  return merged;
}

export function hasParallelProcessingAncestorLoop(
  step: any,
  stepList: any[]
): boolean {
  if (!step || !Array.isArray(stepList)) return false;

  return getLoopStackForStep(step, stepList).some((loopOrigId) => {
    const found = findStepByOrigId(stepList, loopOrigId);
    return normalizeLoopConfig(found?.step?.loopConfig, found?.step?.loopType)
      .parallelProcessing;
  });
}

export function hasParallelProcessingDescendantLoop(
  stepList: any[],
  parentLoopOrigId: string
): boolean {
  if (!Array.isArray(stepList) || !parentLoopOrigId) return false;

  let hasParallelDescendant = false;

  walkFlowSteps(stepList, (step) => {
    if (hasParallelDescendant) return;
    if (!(checkIsLoop(step) || step?.type === "LOOP")) return;
    if (step?.origId === parentLoopOrigId) return;

    const loopStack = getLoopStackForStep(step, stepList);
    if (!loopStack.includes(parentLoopOrigId)) return;

    hasParallelDescendant = normalizeLoopConfig(
      step.loopConfig,
      step.loopType
    ).parallelProcessing;
  });

  return hasParallelDescendant;
}

function walkFlowSteps(stepList: any[], callback: (step: any) => void): void {
  if (!Array.isArray(stepList)) return;

  stepList.forEach((step) => {
    callback(step);

    if (Array.isArray(step?.routes)) {
      step.routes.forEach((route: any) => {
        walkFlowSteps(route?.steps, callback);
      });
    }
  });
}

/**
 * Gets the steps array from a loop's uiData.
 */
export function getLoopSteps(
  step: { uiData?: string } | null | undefined
): Array<{ loopStepIndex: number; origId: string }> {
  const uiData = parseUiData(step?.uiData);
  return uiData?.steps ?? [];
}

/**
 * Gets the number of steps inside a loop.
 */
export function getLoopStepCount(
  step: { uiData?: string } | null | undefined
): number {
  return getLoopSteps(step).length;
}

/**
 * Determines if a step should be treated as part of a loop.
 * Covers loop steps, loop start markers, and steps that live inside routers that are themselves inside loops.
 */
export function isLoopStepOrInsideLoop(
  step: { uiData?: string } | null | undefined,
  stepList: any[] | null | undefined
): boolean {
  if (!step) return false;
  if (checkIsLoopEnd(step)) return false;
  if (checkIsLoopStep(step) || checkIsLoop(step)) return true;

  if (!Array.isArray(stepList) || stepList.length === 0) {
    return false;
  }

  const context = getLoopContextForStep(step, stepList);
  return context.inLoop;
}
